import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import TotalEnergiesContext from "../../store/context/TotalEnergiesContext";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import {
  getCifCupsFromLocalStorage,
  getEntitySelectionFromLocalStorage,
  getOwnerDataFromLocalStorage,
  setEntitySelectionToLocalStorage,
} from "../../services/localStorage/localStorage.service";
import { cloneDeep, isEmpty, isNil, uniq } from "lodash";
import { getEntityOptions } from "../../services/masterFilter/masterFilter.service";
import { EntityOptionsClients, EntityOptionsRequestBody } from "../../models/entitySelector/entitySelector.model";
import {
  getCifCups,
  getCifCupsForAgent,
} from "../../services/cupsCifSelector/cupsCifSelector.service";
import {
  clearFirstLoadCifCups,
  formatCifCups,
} from "../../../utils/components/cupsCifSelector/cupsCifSelector.util";
import { CifCupsAgent } from "../../models/cupsCifSelector/cupsCifSelector.model";
import { mapAgentMasterResponseToOwnerData } from "../../../utils/agentData/agentData.utils";

const PAGE_SIZE_STEP = 10000;
const CUPS_PAGE_SIZE_STEP = 1000;

const useAgentData = () => {
  const {
    isEntitySelected,
    setIsEntitySelected,
    isAgent,
    selectedEntityList,
    setSelectedEntityList,
    storageCifCupsState,
    storageOwnerData,
  } = useContext(TotalEnergiesContext);

  const navigate = useNavigate();
  const location = useLocation();

  const [loadingState, setLoadingState] = useState({
    entityOptions: false,
    downloadCifCups: false,
    cifCupsAgent: false,
  });

  const [entityOptions, setEntityOptions] = useState<EntityOptionsClients[]>([]);

  const isEntitySelectionPage = useMemo(
    () => location.pathname === process.env.REACT_APP_ENTITY_SELECTOR_PAGE!,
    [location.pathname],
  );
  const storedEntities = getEntitySelectionFromLocalStorage();

  useEffect(() => {
    if (isAgent) {
      if (storedEntities?.selection && !isEmpty(storedEntities?.selection)) {
        setSelectedEntityList(storedEntities.selection);
        setIsEntitySelected(true);
        const savedOwnerData = getOwnerDataFromLocalStorage();
        const savedCifCups = getCifCupsFromLocalStorage();
        if (
          !isEntitySelectionPage &&
          (isNil(savedOwnerData) || isNil(savedCifCups))
        ) {
          handleGetCupsForAgent(storedEntities.selection);
        }
      } else {
        const isEntitySelectionRequired = !isEntitySelected;

        // ? Expiration check (not sure yet if needed)
        // if (storedEntities?.savedOn && !isEmpty(storedEntities?.savedOn)) {
        //   const isExpiredList =
        //     dayjs().diff(
        //       dayjs(storedEntities.savedOn, DATE_FORMATS.LONG_DATE),
        //       "day"
        //     ) >= MAX_ENTITY_SELECTION_DAYS;

        //   isEntitySelectionRequired = isExpiredList;
        // }

        if (isEntitySelectionRequired) {
          if (!isEntitySelectionPage) {
            const searchParams = createSearchParams({
              from: location.pathname,
            });

            navigate(
              process.env.REACT_APP_ENTITY_SELECTOR_PAGE! +
                `?${searchParams.toString()}`,
            );
          }
        } else {
          setIsEntitySelected(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgent]);

  const handleGetEntityOptions = useCallback(
    async (abortSignal: AbortSignal) => {
      try {
        setLoadingState((prev) => ({ ...prev, entityOptions: true }));

        const initialRequestBody: EntityOptionsRequestBody = {
          pageIndex: 0,
          pageSize: PAGE_SIZE_STEP,
        };

        const response = await getEntityOptions(initialRequestBody, {
          signal: abortSignal,
        });

        const addSavedOptions = (options: EntityOptionsClients[]) => {
          setEntityOptions((prev) => uniq([...prev, ...options]));
        };

        if (response.Clientes && !isEmpty(response.Clientes)) {
          addSavedOptions(response.Clientes);
        }

        if (response.TotalItems > PAGE_SIZE_STEP) {
          let promises = [];

          const totalItems = response.TotalItems || 0;
          const totalPages = Math.ceil(totalItems / PAGE_SIZE_STEP);
          for (let pageIndex = 1; pageIndex < totalPages; pageIndex++) {
            const requestBody: EntityOptionsRequestBody = {
              pageIndex,
              pageSize: PAGE_SIZE_STEP,
            };
            promises.push(
              getEntityOptions(requestBody, { signal: abortSignal }),
            );
          }

          const results = await Promise.all(promises);

          const successes = results.filter((res) => "Clientes" in res);

          successes.forEach((res) => {
            if (res.Clientes && !isEmpty(res.Clientes)) {
              addSavedOptions(res.Clientes);
            }
          });
        }
      } catch (error) {
        console.log("Error retrieving entityOprions");
      } finally {
        setLoadingState((prev) => ({ ...prev, entityOptions: false }));
      }
    },
    [],
  );

  const handleSetSelectedEntities = useCallback(
    (entities: string[]) => {
      setSelectedEntityList(entities);
      setIsEntitySelected(!isEmpty(entities));
      setEntitySelectionToLocalStorage(entities);
    },
    [setIsEntitySelected, setSelectedEntityList],
  );

  /**
   * Cups fetching abort controller.
   * in case of error of any of the cups fetching calls, abort the other one.
   */
  const cupsAbortController = useMemo(() => new AbortController(), []);

  const handleGetCifCupsForAgent = useCallback(
    async (selectedEntities: string[]) => {
      setLoadingState((prev) => ({ ...prev, cifCupsAgent: true }));

      try {
        const cupList: CifCupsAgent[] = [];
        const cupsResponse = await getCifCupsForAgent(
          {
            identities: selectedEntities,
            pageIndex: 0,
            pageSize: CUPS_PAGE_SIZE_STEP,
          },
          "",
          cupsAbortController.signal,
        );

        const isResponseEmpty =
          isEmpty(cupsResponse) ||
          isEmpty(cupsResponse.Cups) ||
          cupsResponse.TotalItems === 0;

        if (isResponseEmpty) {
          return getCupsCallErrorResponse();
        }

        cupList.push(...cloneDeep(cupsResponse.Cups));
        if (cupsResponse.TotalItems > CUPS_PAGE_SIZE_STEP) {
          const promises = [];
          for (
            let pageIndex = 1;
            pageIndex <
            Math.ceil(cupsResponse.TotalItems / CUPS_PAGE_SIZE_STEP);
            pageIndex++
          ) {
            promises.push(
              getCifCupsForAgent(
                {
                  identities: selectedEntities,
                  pageIndex,
                  pageSize: CUPS_PAGE_SIZE_STEP,
                },
                "",
                cupsAbortController.signal,
              ),
            );
          }
          const results = await Promise.all(promises);
          const successes = results.filter((res) => "Cups" in res);

          successes.forEach((pagedCupsResponse) => {
            cupList.push(...cloneDeep(pagedCupsResponse.Cups));
          });
        }

        storageOwnerData(mapAgentMasterResponseToOwnerData(cupList));
      } catch (error) {
        cupsAbortController.abort();
        throw error;
      } finally {
        setLoadingState((prev) => ({ ...prev, cifCupsAgent: false }));
      }
    },
    [cupsAbortController, storageOwnerData],
  );

  const handleGetDownloadCupsForAgent = useCallback(
    async (selectedEntities: string[]) => {
      setLoadingState((prev) => ({ ...prev, downloadCifCups: true }));

      try {
        const downloadCups = await getCifCups(
          {
            isAdmin: true,
            userid: "0",
            identities: selectedEntities,
          },
          "",
          cupsAbortController.signal,
        );

        if (isEmpty(downloadCups)) {
          return getCupsCallErrorResponse();
        }

        const formattedCifCups = formatCifCups(downloadCups, []);

        storageCifCupsState(clearFirstLoadCifCups(formattedCifCups));
      } catch (error) {
        cupsAbortController.abort();
        throw error;
      } finally {
        setLoadingState((prev) => ({ ...prev, downloadCifCups: false }));
      }
    },
    [cupsAbortController, storageCifCupsState],
  );

  const handleGetCupsForAgent = useCallback(
    async (selectedValues: string[]) => {
      const results = await Promise.allSettled([
        handleGetCifCupsForAgent(selectedValues),
        handleGetDownloadCupsForAgent(selectedValues),
      ]);

      return results;
    },
    [handleGetCifCupsForAgent, handleGetDownloadCupsForAgent],
  );

  return {
    selectedEntityList,
    setSelectedEntityList: handleSetSelectedEntities,
    isEntitySelectionPage,
    entityOptions,
    fetchEntityOptions: handleGetEntityOptions,
    loadingState,
    getCupsForAgent: handleGetCupsForAgent,
  };
};

export default useAgentData;

function getCupsCallErrorResponse() {
  return {
    emptyCupList: true,
  };
}
